// The find keys (Ctrl/Cmd-F, Ctrl/Cmd-G, Shift-Ctrl/Cmd-G) for the
// document viewer. pdf.js answers them inside the frame by opening its own
// find bar, which fp-chrome.css hides in favour of the reading aids' Find,
// so they are taken before pdf.js sees them and sent to the reading aids.

export type FindShortcut = "open" | "next" | "previous";

/** The find action a keydown asks for, or null when it is not a find key. */
export const findShortcutOf = (
  event: Pick<
    KeyboardEvent,
    "key" | "code" | "ctrlKey" | "metaKey" | "altKey" | "shiftKey"
  >
): FindShortcut | null => {
  if (event.altKey || event.ctrlKey === event.metaKey) return null;
  // The letter typed, so Ctrl-U on a Dvorak layout (whose U sits on the
  // physical F) stays View Source; the physical key only for a layout that
  // types no Latin letter there (Cyrillic, Greek), as pdf.js's keyCode does.
  const typed = event.key.toLowerCase();
  const key = /^[a-z]$/.test(typed)
    ? typed
    : event.code.replace(/^Key/, "").toLowerCase();
  if (key === "f" && !event.shiftKey) return "open";
  if (key === "g") {
    return event.shiftKey ? "previous" : "next";
  }
  return null;
};

/**
 * Take the find keys inside the viewer frame. `onShortcut` answers whether
 * the reading aids acted on the key; when they did not (a compact frame has
 * no Find), the key still never reaches pdf.js, so the browser's own find
 * opens rather than pdf.js's hidden one.
 */
export const listenForViewerFindShortcut = (
  iframe: HTMLIFrameElement,
  onShortcut: (shortcut: FindShortcut) => boolean
): (() => void) => {
  // Null, without throwing, when the frame is on another origin's page,
  // which takes no listener from us (#14962).
  const frameDocument = iframe.contentDocument;
  const frameWindow = iframe.contentWindow;
  if (!frameDocument || !frameWindow) return () => undefined;

  const handleKeyDown = (event: KeyboardEvent) => {
    const shortcut = findShortcutOf(event);
    if (shortcut === null) return;
    // Capture at the frame's window runs before pdf.js's own keydown
    // listener there.
    event.stopImmediatePropagation();
    if (onShortcut(shortcut)) event.preventDefault();
  };

  frameWindow.addEventListener("keydown", handleKeyDown, true);
  return () => {
    // The frame may have navigated to a page we cannot touch; the listener
    // went away with the old page (#14962). On unmount the frame is already
    // detached, so contentDocument is null and the skip is right there too.
    if (iframe.contentDocument !== frameDocument) return;
    frameWindow.removeEventListener("keydown", handleKeyDown, true);
  };
};
